import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Mail, Leaf, Check, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { apiRequest } from "@/lib/queryClient";

// Validation schema
const newsletterSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  firstName: z.string().optional(),
  marketingConsent: z.boolean().default(true),
  careRemindersConsent: z.boolean().default(true),
});

type NewsletterFormData = z.infer<typeof newsletterSchema>;

interface NewsletterSignupProps {
  source: "homepage" | "footer" | "checkout" | "account" | "popup";
  variant?: "default" | "minimal" | "inline" | "card";
  title?: string;
  description?: string;
  showName?: boolean;
  showConsents?: boolean;
  biomePreference?: string;
  className?: string;
  onSuccess?: () => void;
}

export function NewsletterSignup({
  source,
  variant = "default",
  title = "Join Our Newsletter",
  description = "Get terrarium care tips, exclusive offers, and first access to new designs.",
  showName = false,
  showConsents = false,
  biomePreference,
  className = "",
  onSuccess,
}: NewsletterSignupProps) {
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterSchema),
    defaultValues: {
      marketingConsent: true,
      careRemindersConsent: true,
    },
  });

  const subscribeMutation = useMutation({
    mutationFn: async (data: NewsletterFormData) => {
      const response = await apiRequest("POST", "/api/newsletter/subscribe", {
        ...data,
        source,
        biomePreference,
        orderUpdatesConsent: true,
      });
      return response.json();
    },
    onSuccess: (data) => {
      setIsSuccess(true);
      reset();
      toast({
        title: "Welcome to Life Itself Studio!",
        description: data.pendingConfirmation
          ? "Please check your email to confirm your subscription."
          : "You're now subscribed to our newsletter.",
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Subscription failed",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: NewsletterFormData) => {
    subscribeMutation.mutate(data);
  };

  const marketingConsent = watch("marketingConsent");
  const careRemindersConsent = watch("careRemindersConsent");

  // Success state
  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`flex flex-col items-center justify-center p-6 text-center ${className}`}
      >
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Check className="w-6 h-6 text-primary" />
        </div>
        <h3 className="font-serif text-lg font-medium mb-2">You're In!</h3>
        <p className="text-muted-foreground text-sm">
          Check your inbox to confirm your subscription.
        </p>
      </motion.div>
    );
  }

  // Minimal inline variant (for footer)
  if (variant === "minimal" || variant === "inline") {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className={`flex gap-2 ${className}`}>
        <div className="flex-1">
          <Input
            {...register("email")}
            type="email"
            placeholder="Enter your email"
            aria-label="Email address for newsletter"
            className={errors.email ? "border-destructive" : ""}
            disabled={subscribeMutation.isPending}
          />
          {errors.email && (
            <p className="text-destructive text-xs mt-1">{errors.email.message}</p>
          )}
        </div>
        <Button
          type="submit"
          disabled={subscribeMutation.isPending}
          size="default"
        >
          {subscribeMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Mail className="w-4 h-4 mr-2" />
              Subscribe
            </>
          )}
        </Button>
      </form>
    );
  }

  // Card variant (for homepage/checkout sections)
  if (variant === "card") {
    return (
      <div
        className={`bg-card border rounded-xl p-6 ${className}`}
        role="region"
        aria-label="Newsletter signup"
      >
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Leaf className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-serif text-lg font-medium mb-1">{title}</h3>
            <p className="text-muted-foreground text-sm">{description}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {showName && (
            <div>
              <Label htmlFor="firstName">First Name (Optional)</Label>
              <Input
                {...register("firstName")}
                id="firstName"
                placeholder="Your first name"
                disabled={subscribeMutation.isPending}
              />
            </div>
          )}

          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              {...register("email")}
              id="email"
              type="email"
              placeholder="you@example.com"
              aria-describedby="email-error"
              className={errors.email ? "border-destructive" : ""}
              disabled={subscribeMutation.isPending}
            />
            {errors.email && (
              <p id="email-error" className="text-destructive text-xs mt-1">
                {errors.email.message}
              </p>
            )}
          </div>

          {showConsents && (
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="marketingConsent"
                  checked={marketingConsent}
                  onCheckedChange={(checked) =>
                    setValue("marketingConsent", checked as boolean)
                  }
                  disabled={subscribeMutation.isPending}
                />
                <Label
                  htmlFor="marketingConsent"
                  className="text-sm font-normal text-muted-foreground leading-tight"
                >
                  Receive marketing emails about new products and offers
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <Checkbox
                  id="careRemindersConsent"
                  checked={careRemindersConsent}
                  onCheckedChange={(checked) =>
                    setValue("careRemindersConsent", checked as boolean)
                  }
                  disabled={subscribeMutation.isPending}
                />
                <Label
                  htmlFor="careRemindersConsent"
                  className="text-sm font-normal text-muted-foreground leading-tight"
                >
                  Receive terrarium care tips and seasonal reminders
                </Label>
              </div>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={subscribeMutation.isPending}
          >
            {subscribeMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Subscribing...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Subscribe to Newsletter
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </form>
      </div>
    );
  }

  // Default variant
  return (
    <div className={className} role="region" aria-label="Newsletter signup">
      <div className="text-center mb-6">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Leaf className="w-6 h-6 text-primary" />
        </div>
        <h3 className="font-serif text-xl font-medium mb-2">{title}</h3>
        <p className="text-muted-foreground">{description}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-md mx-auto">
        {showName && (
          <div>
            <Label htmlFor="firstName">First Name (Optional)</Label>
            <Input
              {...register("firstName")}
              id="firstName"
              placeholder="Your first name"
              disabled={subscribeMutation.isPending}
            />
          </div>
        )}

        <div>
          <Label htmlFor="email">Email Address</Label>
          <Input
            {...register("email")}
            id="email"
            type="email"
            placeholder="you@example.com"
            aria-describedby="email-error-default"
            className={errors.email ? "border-destructive" : ""}
            disabled={subscribeMutation.isPending}
          />
          {errors.email && (
            <p id="email-error-default" className="text-destructive text-xs mt-1">
              {errors.email.message}
            </p>
          )}
        </div>

        {showConsents && (
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <Checkbox
                id="marketingConsentDefault"
                checked={marketingConsent}
                onCheckedChange={(checked) =>
                  setValue("marketingConsent", checked as boolean)
                }
                disabled={subscribeMutation.isPending}
              />
              <Label
                htmlFor="marketingConsentDefault"
                className="text-sm font-normal text-muted-foreground leading-tight"
              >
                Receive marketing emails about new products and offers
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="careRemindersConsentDefault"
                checked={careRemindersConsent}
                onCheckedChange={(checked) =>
                  setValue("careRemindersConsent", checked as boolean)
                }
                disabled={subscribeMutation.isPending}
              />
              <Label
                htmlFor="careRemindersConsentDefault"
                className="text-sm font-normal text-muted-foreground leading-tight"
              >
                Receive terrarium care tips and seasonal reminders
              </Label>
            </div>
          </div>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={subscribeMutation.isPending}
        >
          {subscribeMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Subscribing...
            </>
          ) : (
            <>
              <Mail className="w-4 h-4 mr-2" />
              Subscribe to Newsletter
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          We respect your privacy. Unsubscribe at any time.
        </p>
      </form>
    </div>
  );
}
